import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const API_ROOT = "/api/admin/support-chat";

const buildQuery = (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") queryParams.append(key, value);
    });
    const qs = queryParams.toString();
    return qs ? `?${qs}` : "";
};

export const supportChatSlice = createSlice({
    name: "supportChat",
    initialState: {
        rooms: [],
        roomsLoading: false,
        roomsPagination: null,

        activeRoom: null,
        activeRoomLoading: false,
        messages: [],

        summary: null,

        sendMessageLoading: false,
        sendMessageFail: false,
        sendMessageError: null,

        statusChangeSuccess: false,
        statusChangeFail: false,
        statusChangeError: null,

        unread: { unread_rooms: 0, unread_messages: 0 },

        // True once the Firestore listeners are attached; the page stops polling
        // when this flips.
        realtimeConnected: false,
    },
    reducers: {
        setRoomsLoading: (state) => { state.roomsLoading = true; },
        doGetRoomsSuccess: (state, action) => {
            state.rooms = action.payload.data || [];
            state.roomsPagination = {
                page: action.payload.current_page,
                pages: action.payload.page_count,
                total_row: action.payload.total_row,
            };
            state.roomsLoading = false;
        },
        doGetRoomsFail: (state) => { state.rooms = []; state.roomsPagination = null; state.roomsLoading = false; },

        setActiveRoomLoading: (state) => { state.activeRoomLoading = true; },
        doGetRoomSuccess: (state, action) => {
            state.activeRoom = action.payload.data || null;
            state.messages = action.payload.messages || [];
            state.activeRoomLoading = false;
        },
        doGetRoomFail: (state) => { state.activeRoom = null; state.messages = []; state.activeRoomLoading = false; },
        clearActiveRoom: (state) => { state.activeRoom = null; state.messages = []; },

        doGetSummarySuccess: (state, action) => {
            state.summary = action.payload || null;
            state.unread = {
                unread_rooms: action.payload?.unread_rooms || 0,
                unread_messages: action.payload?.unread_messages || 0,
            };
        },

        setSendMessageLoading: (state) => { state.sendMessageLoading = true; state.sendMessageFail = false; },
        doSendMessageSuccess: (state, action) => {
            state.sendMessageLoading = false;
            // Firestore normally delivers the message a moment later; appending
            // it here keeps the thread responsive, and the listener dedupes by id.
            const message = action.payload?.data;
            if (message && !state.messages.some((m) => Number(m.id) === Number(message.id))) {
                state.messages.push(message);
            }
        },
        doSendMessageFail: (state, action) => { state.sendMessageLoading = false; state.sendMessageFail = true; state.sendMessageError = action.payload || null; },
        resetSendMessageState: (state) => { state.sendMessageFail = false; state.sendMessageError = null; },

        doSetStatusSuccess: (state, action) => { state.statusChangeSuccess = true; state.statusChangeFail = false; state.activeRoom = action.payload?.data || state.activeRoom; },
        doSetStatusFail: (state, action) => { state.statusChangeSuccess = false; state.statusChangeFail = true; state.statusChangeError = action.payload || null; },
        resetStatusChangeState: (state) => { state.statusChangeSuccess = false; state.statusChangeFail = false; state.statusChangeError = null; },


        // Firestore pushes
        setRealtimeRooms: (state, action) => {
            state.rooms = action.payload || [];
            state.roomsLoading = false;
        },
        setRealtimeMessages: (state, action) => {
            state.messages = action.payload || [];
        },
        setRealtimeConnected: (state, action) => { state.realtimeConnected = Boolean(action.payload); },
    },
});

export const doGetSupportRooms = (params = {}) => async (dispatch) => {
    try {
        dispatch(setRoomsLoading());
        const response = await axios.get(`${API_ROOT}/rooms${buildQuery(params)}`);
        dispatch(doGetRoomsSuccess(response.data));
    } catch (error) { dispatch(doGetRoomsFail()); }
};

export const doGetSupportRoom = (roomId, params = {}) => async (dispatch) => {
    try {
        dispatch(setActiveRoomLoading());
        const response = await axios.get(`${API_ROOT}/rooms/${roomId}${buildQuery(params)}`);
        dispatch(doGetRoomSuccess(response.data));
    } catch (error) { dispatch(doGetRoomFail()); }
};

export const doGetSupportSummary = () => async (dispatch) => {
    try {
        const response = await axios.get(`${API_ROOT}/summary`);
        dispatch(doGetSummarySuccess(response.data));
    } catch (error) { /* header counters only */ }
};

export const doSendSupportMessage = (roomId, formData) => async (dispatch) => {
    try {
        dispatch(setSendMessageLoading());
        const response = await axios.post(`${API_ROOT}/rooms/${roomId}/messages`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        dispatch(doSendMessageSuccess(response.data));
    } catch (error) { dispatch(doSendMessageFail(error.response?.data?.errorMessage || null)); }
};

export const doMarkSupportRoomRead = (roomId) => async (dispatch) => {
    try {
        await axios.put(`${API_ROOT}/rooms/${roomId}/read`);
        dispatch(doGetSupportSummary());
    } catch (error) { /* a failed read receipt is not worth surfacing */ }
};

export const doSetSupportRoomStatus = (roomId, status) => async (dispatch) => {
    try {
        const response = await axios.put(`${API_ROOT}/rooms/${roomId}/status`, { status });
        dispatch(doSetStatusSuccess(response.data));
    } catch (error) {
        // Carry the server's own wording through — a silent failure here reads
        // as "the button does nothing".
        const detail = error.response
            ? `${error.response.status} ${error.response.data?.errorMessage || ""}`.trim()
            : error.message;
        dispatch(doSetStatusFail(detail));
    }
};

export const {
    setRoomsLoading, doGetRoomsSuccess, doGetRoomsFail,
    setActiveRoomLoading, doGetRoomSuccess, doGetRoomFail, clearActiveRoom,
    doGetSummarySuccess,
    setSendMessageLoading, doSendMessageSuccess, doSendMessageFail, resetSendMessageState,
    doSetStatusSuccess, doSetStatusFail, resetStatusChangeState,
    setRealtimeRooms, setRealtimeMessages, setRealtimeConnected,
} = supportChatSlice.actions;

export const getSupportRooms = (state) => state.supportChat?.rooms;
export const getSupportRoomsLoading = (state) => state.supportChat?.roomsLoading;
export const getSupportActiveRoom = (state) => state.supportChat?.activeRoom;
export const getSupportActiveRoomLoading = (state) => state.supportChat?.activeRoomLoading;
export const getSupportMessages = (state) => state.supportChat?.messages;
export const getSupportSummary = (state) => state.supportChat?.summary;
export const getSupportSendMessageLoading = (state) => state.supportChat?.sendMessageLoading;
export const getSupportSendMessageFail = (state) => state.supportChat?.sendMessageFail;
export const getSupportSendMessageError = (state) => state.supportChat?.sendMessageError;
export const getSupportUnread = (state) => state.supportChat?.unread;
export const getSupportStatusChangeFail = (state) => state.supportChat?.statusChangeFail;
export const getSupportStatusChangeError = (state) => state.supportChat?.statusChangeError;
export const getSupportRealtimeConnected = (state) => state.supportChat?.realtimeConnected;

export default supportChatSlice.reducer;
